import React from 'react';
import type { QueryClient } from '@tanstack/react-query';

import {
  type ConversationComposerDraftStore,
  isViewerQueryKey,
} from '@/components/modules/Chatbot/workspace/conversationComposerDrafts';
import PageLink from '@/utils/PageLink';

/**
 * Keeps unsent saved-chat text across an ordinary same-document page change out of Atlas and back, for the same
 * sign-in. It owns, for the App's lifetime, at most one registered Atlas host and at most one retained copy of that
 * host's text. Nothing intercepts Back/Forward, router methods or History, and nothing is stored outside memory:
 * reloading or closing the tab still loses the text (after the browser's standard warning).
 *
 * The copy is taken only for a page change that actually commits: just before React applies it
 * (`getSnapshotBeforeUpdate`, observational), and kept only if, once the outside page has committed
 * (`componentDidUpdate`), the same host, session and store generation still hold. An observed change of the
 * authenticated scope (viewer, role, effective owner, Atlas access or seat), removal of the viewer query (logout, cache
 * clear, garbage collection), an auth page or a new QueryClient ends the session at once: the registered host's text and
 * any retained copy are cleared before React renders anything for the next scope. Every session end is then announced
 * through `AtlasRouteDraftSessionContext`, so each host re-renders and registers again under the new epoch and client
 * without waiting for unrelated renders. Unmounting the boundary revokes the same way, before any passive cleanup.
 */

/** What a mounted Atlas host offers the boundary (see `useAtlasExpertChat`). */
export interface AtlasRouteDraftRegistration {
  store: ConversationComposerDraftStore;
  /** `atlasRouteDraftScope` of the host's committed viewer. */
  scope: string;
  /** `epoch()` when the host rendered. */
  epoch: number;
  client: QueryClient;
}

/** A returning host's request for the retained text; honoured once, only for the same session and scope. */
export interface AtlasRouteDraftClaim {
  scope: string;
  epoch: number;
  client: QueryClient;
}

export interface AtlasRouteDraftController {
  /** The boundary's session epoch; it changes whenever the observed authenticated session ends. */
  epoch(): number;
  /** Registers a committed host; returns the cleanup that withdraws exactly this registration. */
  register(registration: AtlasRouteDraftRegistration): () => void;
  /** Hands the retained store to a committed returning host (once), or null. */
  claim(request: AtlasRouteDraftClaim): ConversationComposerDraftStore | null;
}

export const AtlasRouteDraftContext = React.createContext<AtlasRouteDraftController | null>(null);

/**
 * A notice that changes whenever the boundary's session ends or is re-established (a new client, a StrictMode replay).
 * A host reads it only to re-render, then registers again with the controller's current `epoch()`.
 */
export const AtlasRouteDraftSessionContext = React.createContext(0);

/**
 * The authenticated scope recovery belongs to, from the viewer query's data, or null when it cannot authorize recovery:
 * no id, a role without an effective owner (SUPER_ADMIN, USER, missing), or an observed loss of access (banned,
 * deleted, a frozen or removed seat, Atlas access refused). A profile-name change keeps the same scope.
 */
export function atlasRouteDraftScope(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null;
  const viewer = user as {
    id?: unknown;
    type?: unknown;
    teamOwnerId?: unknown;
    seatState?: unknown;
    banned?: unknown;
    deletedById?: unknown;
    atlasAccess?: unknown;
  };
  const id = typeof viewer.id === 'string' && viewer.id ? viewer.id : null;
  if (!id) return null;
  const owner =
    viewer.type === 'BUSINESS_OWNER'
      ? id
      : viewer.type === 'ADMIN' || viewer.type === 'TEAM_MEMBER'
        ? typeof viewer.teamOwnerId === 'string' && viewer.teamOwnerId
          ? viewer.teamOwnerId
          : null
        : null;
  if (!owner) return null;
  if (
    viewer.banned === true ||
    viewer.atlasAccess === false ||
    viewer.seatState === 'frozen' ||
    viewer.seatState === 'removed' ||
    (typeof viewer.deletedById === 'string' && viewer.deletedById)
  )
    return null;
  return JSON.stringify([
    id,
    viewer.type,
    owner,
    viewer.atlasAccess === true,
    typeof viewer.seatState === 'string' ? viewer.seatState : null,
  ]);
}

/** The committed page that hosts the Atlas expert chat (the path without query or hash). */
function isAtlasPage(path: string): boolean {
  return path === PageLink.atlasHome;
}

/** Pages that end the session as soon as they commit (sign-in and revoked-access pages). */
function isAuthPage(path: string): boolean {
  return path.startsWith('/auth/') || path === '/auth' || path === '/access-revoked';
}

interface Occurrence {
  readonly store: ConversationComposerDraftStore;
  readonly scope: string;
  readonly epoch: number;
  readonly client: QueryClient;
  /** Set by this occurrence's own cleanup (or when its session ended). */
  withdrawn: boolean;
}

interface Retained {
  readonly store: ConversationComposerDraftStore;
  readonly scope: string;
  readonly epoch: number;
  readonly client: QueryClient;
  onBeforeUnload: ((event: BeforeUnloadEvent) => void) | null;
}

interface DepartureSnapshot {
  readonly kind: 'departure';
  readonly occurrence: Occurrence;
  /** The registration serial when the copy was taken: any registration after it (even withdrawn) rejects the copy. */
  readonly serial: number;
  readonly sourceGeneration: number;
  /** Private copy; cleared unless it qualifies once the outside page has committed. */
  readonly copy: ConversationComposerDraftStore;
  readonly scope: string;
  readonly epoch: number;
  readonly client: QueryClient;
}

/** A QueryClient replacement: the host registered under the old client, captured before its cleanup runs. */
interface ClientSnapshot {
  readonly kind: 'client';
  readonly previous: Occurrence | null;
}

export interface AtlasRouteDraftBoundaryProps {
  queryClient: QueryClient;
  /** The committed page's path, without query or hash. */
  path: string;
  children?: React.ReactNode;
}

interface AtlasRouteDraftBoundaryState {
  /** Changed on every session end, so hosts re-render and register again. */
  notice: number;
}

export class AtlasRouteDraftBoundary extends React.Component<
  AtlasRouteDraftBoundaryProps,
  AtlasRouteDraftBoundaryState
> {
  state: AtlasRouteDraftBoundaryState = { notice: 0 };
  private client: QueryClient;
  private scope: string | null;
  private sessionEpoch = 0;
  /** Increases with every accepted registration (never reused). */
  private registrationSerial = 0;
  private active: Occurrence | null = null;
  private retained: Retained | null = null;
  /** The page of the commit in progress (set before React applies it, so a returning host's layout claim sees it). */
  private committingPath: string;
  private unsubscribe: (() => void) | null = null;
  /** Between mount and unmount; announcements and registrations happen only then. */
  private mounted = false;
  /** Set by unmount (or a StrictMode simulated unmount) until the next mount. */
  private disposed = false;
  /** Stable for this boundary instance across every page render. */
  private readonly controller: AtlasRouteDraftController;

  constructor(props: AtlasRouteDraftBoundaryProps) {
    super(props);
    this.client = props.queryClient;
    this.scope = atlasRouteDraftScope(props.queryClient.getQueryData(['user']));
    this.committingPath = props.path;
    this.controller = {
      epoch: () => this.sessionEpoch,
      register: (registration) => this.register(registration),
      claim: (request) => this.claim(request),
    };
  }

  componentDidMount() {
    const reestablished = this.disposed;
    this.mounted = true;
    this.disposed = false;
    this.observe();
    // After a StrictMode simulated unmount the hosts' registrations were refused: let them register again.
    if (reestablished) this.announce();
  }

  getSnapshotBeforeUpdate(
    prevProps: AtlasRouteDraftBoundaryProps
  ): DepartureSnapshot | ClientSnapshot | null {
    this.committingPath = this.props.path;
    if (prevProps.queryClient !== this.props.queryClient)
      return { kind: 'client', previous: this.active };
    if (!isAtlasPage(prevProps.path) || isAtlasPage(this.props.path)) return null;
    if (isAuthPage(this.props.path)) return null;
    const host = this.active;
    if (!host || host.withdrawn || host.client !== this.client) return null;
    if (host.epoch !== this.sessionEpoch || !this.scope || host.scope !== this.scope) return null;
    if (this.liveScope() !== this.scope || !host.store.hasDrafts()) return null;
    const { sourceGeneration, store: copy } = host.store.fork();
    return {
      kind: 'departure',
      occurrence: host,
      serial: this.registrationSerial,
      sourceGeneration,
      copy,
      scope: this.scope,
      epoch: this.sessionEpoch,
      client: this.client,
    };
  }

  componentDidUpdate(
    prevProps: AtlasRouteDraftBoundaryProps,
    _prevState: AtlasRouteDraftBoundaryState,
    snapshot: DepartureSnapshot | ClientSnapshot | null
  ) {
    if (prevProps.queryClient !== this.props.queryClient) {
      // The old client's host store is cleared by value, even if its own cleanup already withdrew it this commit.
      if (snapshot?.kind === 'client') snapshot.previous?.store.clear();
      this.client = this.props.queryClient;
      this.endSession();
      this.observe();
    }
    if (prevProps.path !== this.props.path && isAuthPage(this.props.path)) this.endSession();
    if (snapshot?.kind !== 'departure') return;
    const departure = snapshot.occurrence;
    const qualifies =
      snapshot.client === this.client &&
      snapshot.epoch === this.sessionEpoch &&
      snapshot.scope === this.scope &&
      this.liveScope() === snapshot.scope &&
      !isAtlasPage(this.props.path) &&
      !isAuthPage(this.props.path) &&
      // The departed host's own layout cleanup withdrew it, and nothing registered since the copy, even briefly.
      departure.withdrawn &&
      this.active === null &&
      this.registrationSerial === snapshot.serial &&
      // No clear since the copy: a session clear is never mistaken for ordinary cleanup.
      departure.store.generation() === snapshot.sourceGeneration;
    if (!qualifies) {
      snapshot.copy.clear();
      return;
    }
    // The departed host's late callbacks (read, write, send, delete, a held exit) can no longer act on its text.
    departure.store.clear();
    this.retain(snapshot.copy, snapshot.scope);
  }

  componentWillUnmount() {
    // Disposal revokes before any host's passive cleanup: the active host's text, the retained copy, the epoch, and
    // the controller (no registration or claim until a StrictMode remount re-establishes it).
    this.mounted = false;
    this.disposed = true;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.revoke();
  }

  render() {
    return (
      <AtlasRouteDraftContext.Provider value={this.controller}>
        <AtlasRouteDraftSessionContext.Provider value={this.state.notice}>
          {this.props.children}
        </AtlasRouteDraftSessionContext.Provider>
      </AtlasRouteDraftContext.Provider>
    );
  }

  private liveScope(): string | null {
    return atlasRouteDraftScope(this.client.getQueryData(['user']));
  }

  /** Watches the viewer query of the current client; the subscription lives exactly as long as this instance. */
  private observe() {
    this.unsubscribe?.();
    const client = this.client;
    this.unsubscribe = client.getQueryCache().subscribe((event) => {
      if (client !== this.client || !isViewerQueryKey(event.query.queryKey)) return;
      if (event.type === 'removed') {
        this.endSession();
        return;
      }
      const next = atlasRouteDraftScope(event.query.state.data);
      if (next !== this.scope) this.endSession();
    });
    // A change between construction and subscription still counts.
    if (this.liveScope() !== this.scope) this.endSession();
  }

  /** Ends the observed session, then tells every host to register again. */
  private endSession() {
    this.revoke();
    this.announce();
  }

  /** A new epoch, and no text of the old one stays reachable (the active host's store and the retained copy). */
  private revoke() {
    this.sessionEpoch += 1;
    this.scope = this.liveScope();
    const active = this.active;
    this.active = null;
    if (active) {
      active.withdrawn = true;
      active.store.clear();
    }
    this.dropRetained();
  }

  /** Re-renders the hosts (through the session notice) so they register under the current epoch and client. */
  private announce() {
    if (this.mounted) this.setState((state) => ({ notice: state.notice + 1 }));
  }

  private register(registration: AtlasRouteDraftRegistration): () => void {
    if (
      this.disposed ||
      registration.client !== this.client ||
      registration.epoch !== this.sessionEpoch ||
      !registration.scope ||
      registration.scope !== this.scope ||
      this.liveScope() !== registration.scope
    )
      return () => undefined;
    const occurrence: Occurrence = {
      store: registration.store,
      scope: registration.scope,
      epoch: registration.epoch,
      client: registration.client,
      withdrawn: false,
    };
    this.registrationSerial += 1;
    this.active = occurrence;
    // Withdraws only this occurrence: an older cleanup never clears or qualifies a newer registration.
    return () => {
      occurrence.withdrawn = true;
      if (this.active === occurrence) this.active = null;
    };
  }

  private claim(request: AtlasRouteDraftClaim): ConversationComposerDraftStore | null {
    const record = this.retained;
    if (this.disposed || !record || !isAtlasPage(this.committingPath)) return null;
    if (
      request.client !== this.client ||
      request.epoch !== this.sessionEpoch ||
      request.scope !== this.scope
    )
      return null;
    if (!this.retainedHolds(record)) return null;
    this.retained = null;
    this.detachUnload(record);
    return record.store;
  }

  private retainedHolds(record: Retained): boolean {
    return (
      this.retained === record &&
      record.client === this.client &&
      record.epoch === this.sessionEpoch &&
      record.scope === this.scope &&
      this.liveScope() === record.scope &&
      record.store.hasDrafts()
    );
  }

  /** Keeps the copy, and asks the browser to warn before an unload while it holds text for the current session. */
  private retain(store: ConversationComposerDraftStore, scope: string) {
    this.dropRetained();
    const record: Retained = {
      store,
      scope,
      epoch: this.sessionEpoch,
      client: this.client,
      onBeforeUnload: null,
    };
    this.retained = record;
    if (typeof window === 'undefined' || !store.hasDrafts()) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!this.retainedHolds(record)) return;
      event.preventDefault();
      // Older browsers show the warning only when returnValue is set.
      event.returnValue = true;
    };
    record.onBeforeUnload = onBeforeUnload;
    window.addEventListener('beforeunload', onBeforeUnload);
  }

  private detachUnload(record: Retained) {
    const listener = record.onBeforeUnload;
    record.onBeforeUnload = null;
    if (listener && typeof window !== 'undefined')
      window.removeEventListener('beforeunload', listener);
  }

  private dropRetained() {
    const record = this.retained;
    if (!record) return;
    this.retained = null;
    this.detachUnload(record);
    record.store.clear();
  }
}
